import {map, switchMap} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';

import {
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    ClientStatusTriggersEnum,
    AuthApiService,
    IndividualsService,
    IndividualAddressesService,
    IndividualTaxDetailsService,
    IndividualNationalitiesService,
    IndividualIdentityDetailsService,
    LookupModel,
    CountryModel,
    IndividualDocumentModel,
    IndividualExtendedModel,
    IndividualStatusTriggerModel,
    IndividualDocumentsAtomicService
} from '@spa/api';

import {Helper} from '../../../../shared/classes';

import {IndividualRegistrationFormErrorStatesEnum, PanelClientsAddIndividualStepsEnum} from '../../enums';

import {PersonalDetailsForm} from './personal-details.form';
import {NationalitiesForm} from './nationalities.form';
import {IdentityDetailsForm} from './identity-details.form';
import {TaxDetailsForm} from './tax-details.form';
import {IdentificationNumberForm} from './identification-number.form';
import {AddressesForm} from './addresses.form';
import {ProofOfIdentityForm} from './proof-of-identity.form';
import {ProofOfAddressForm} from './proof-of-address.form';

export class IndividualRegistrationForm {
    private readonly individualsService: IndividualsService;
    private readonly nationalitiesService: IndividualNationalitiesService;
    private readonly identityDetailsService: IndividualIdentityDetailsService;
    private readonly taxDetailsService: IndividualTaxDetailsService;
    private readonly addressesService: IndividualAddressesService;
    private readonly authService: AuthApiService;
    private readonly countries: CountryModel[];
    private readonly identityDocumentTypes: LookupModel;
    private readonly proofOfAddressDocumentTypes: LookupModel;
    private readonly individualDocumentsAtomicService: IndividualDocumentsAtomicService;

    public individual: IndividualExtendedModel;
    public personalDetailsForm: PersonalDetailsForm;
    public nationalitiesForm: NationalitiesForm;
    public identityDetailsForm: IdentityDetailsForm;
    public taxDetailsForm: TaxDetailsForm;
    public identificationNumberForm: IdentificationNumberForm;
    public addressesForm: AddressesForm;
    public proofOfIdentityForm: ProofOfIdentityForm;
    public proofOfAddressForm: ProofOfAddressForm;

    constructor(
        individual: IndividualExtendedModel,
        enrollmentsService: IndividualsService,
        nationalitiesService: IndividualNationalitiesService,
        identityDetailsService: IndividualIdentityDetailsService,
        taxDetailsService: IndividualTaxDetailsService,
        addressesService: IndividualAddressesService,
        authService: AuthApiService,
        countries: CountryModel[],
        identityDocumentTypes: LookupModel,
        proofOfAddressDocumentTypes: LookupModel,
        individualDocumentsAtomicService: IndividualDocumentsAtomicService
    ) {
        this.individual = individual;
        this.individualsService = enrollmentsService;
        this.nationalitiesService = nationalitiesService;
        this.identityDetailsService = identityDetailsService;
        this.countries = countries;
        this.identityDocumentTypes = identityDocumentTypes;
        this.taxDetailsService = taxDetailsService;
        this.addressesService = addressesService;
        this.proofOfAddressDocumentTypes = proofOfAddressDocumentTypes;
        this.authService = authService;
        this.individualDocumentsAtomicService = individualDocumentsAtomicService;
    }

    private initIndividualStep(): void {
        this.personalDetailsForm = new PersonalDetailsForm(
            this.individual,
            this.countries,
            null,
            this.individualsService
        );

        this.nationalitiesForm = new NationalitiesForm(this.individual, this.countries, this.nationalitiesService);

        this.personalDetailsForm.initForm(true, false, true);
        this.nationalitiesForm.initForm();
    }

    private createIndividualStepRequest(): ServerResponse<boolean> {
        const nationalities = this.nationalitiesForm.getValues();

        const personalRequest = this.personalDetailsForm.createRequest(nationalities);

        if (!personalRequest) {
            const nationalitiesRequest = this.nationalitiesForm.createRequest();

            if (!nationalitiesRequest) {
                return Helper.createSuccessResponseObservable<boolean>(true);
            }

            return nationalitiesRequest;
        }

        return personalRequest;
    }

    private initIdentityDetailsStep(): void {
        this.identityDetailsForm = new IdentityDetailsForm(
            this.individual,
            this.identityDocumentTypes,
            this.identityDetailsService
        );

        this.taxDetailsForm = new TaxDetailsForm(this.individual, this.countries, this.taxDetailsService);

        // CRM-5286: the Identification Number block sits on the tax step but belongs to the client,
        // not to a tax record, so it is its own form rather than another control in the taxDetails
        // FormArray.
        // CRM-5328: it is given the tax form so the checkbox can be offered on the rows being typed.
        // Nothing is persisted until the step is saved, and the step is only shown while there is
        // nothing persisted, so individual.taxDetails could never answer that question here.
        this.identificationNumberForm = new IdentificationNumberForm(
            this.individual,
            this.individualsService,
            this.taxDetailsForm
        );

        this.identityDetailsForm.initForm();
        // Before the identification number form, which subscribes to this form's tax rows.
        this.taxDetailsForm.initForm();
        this.identificationNumberForm.initForm();
    }

    private createIdentityDetailsStepRequest(): ServerResponse<boolean> {
        let identityDetailsRequest = this.identityDetailsForm.createRequest();

        if (!identityDetailsRequest) {
            identityDetailsRequest = Helper.createSuccessResponseObservable<boolean>(true);
        }

        return identityDetailsRequest.pipe(
            switchMap((identityDetailsResponse: ServerResponseData<boolean>) => {
                if (identityDetailsResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponseObservable<boolean>(identityDetailsResponse);
                }

                const taxDetailsRequest = this.taxDetailsForm.createRequest()
                    || Helper.createSuccessResponseObservable<boolean>(true);

                // The identification number goes last on purpose. When the client ticked "same as
                // tax number" the backend copies from the persisted tax record, so the tax details
                // have to be saved before it looks for one.
                return taxDetailsRequest.pipe(
                    switchMap((taxDetailsResponse: ServerResponseData<boolean>) => {
                        if (taxDetailsResponse.status !== HttpStatusCodesEnum.Ok
                            && taxDetailsResponse.status !== HttpStatusCodesEnum.NoContent) {
                            return Helper.errorsHelper().createErrorResponseObservable<boolean>(taxDetailsResponse);
                        }

                        return this.identificationNumberForm.createRequest()
                            || Helper.createSuccessResponseObservable<boolean>(true);
                    })
                );
            })
        );
    }

    private initAddressesStep(): void {
        this.addressesForm = new AddressesForm(
            null,
            this.individual.id,
            this.individual.addresses,
            this.countries,
            this.addressesService
        );

        this.addressesForm.initForm();
    }

    private createAddressesStepRequest(): ServerResponse<boolean> {
        const request = this.addressesForm.createRequest();

        if (!request) {
            return Helper.createSuccessResponseObservable<boolean>(true);
        }

        return request;
    }

    private initUploadDocumentsStep(): void {
        this.proofOfIdentityForm = new ProofOfIdentityForm(
            this.individualDocumentsAtomicService,
            this.authService,
            this.identityDocumentTypes
        );

        this.proofOfAddressForm = new ProofOfAddressForm(
            this.proofOfAddressDocumentTypes,
            this.authService,
            this.individualDocumentsAtomicService
        );

        this.proofOfIdentityForm.individual =
            this.proofOfAddressForm.individual =
                this.individual;

        this.proofOfIdentityForm.initFormControl();
        this.proofOfIdentityForm.initDocumentsTuple(this.individual.documents);

        this.proofOfAddressForm.initFormControl();
        this.proofOfAddressForm.initDocument(this.individual.documents);
    }

    private createUploadDocumentsStepRequest(): ServerResponse<boolean> {
        let proofOfIdentityRequest = this.proofOfIdentityForm.createRequest(
            false,
            (typeof this.individual.identityDetails[0] !== 'undefined') ? this.individual.identityDetails[0].id : null
        );

        if (!proofOfIdentityRequest) {
            proofOfIdentityRequest = Helper.createSuccessResponseObservable<IndividualDocumentModel>(null);
        }

        return proofOfIdentityRequest.pipe(
            switchMap((proofOfIdentityResponse: ServerResponseData<IndividualDocumentModel>) => {
                if (proofOfIdentityResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper()
                        .createErrorResponseObservable<IndividualDocumentModel>(proofOfIdentityResponse);
                }

                const proofOfAddressRequest = this.proofOfAddressForm.createRequest(false);

                if (!proofOfAddressRequest) {
                    return Helper.createSuccessResponseObservable<boolean>(true);
                }

                return proofOfAddressRequest.pipe(
                    map((proofOfAddressResponse: ServerResponseData<IndividualDocumentModel>) => {
                        if (proofOfAddressResponse.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper()
                                .createErrorResponse<IndividualDocumentModel>(proofOfAddressResponse);
                        }

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
            })
        );
    }

    private changeIndividualStatus(
        trigger: number,
        response: ServerResponseData<boolean>
    ): ServerResponse<boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return of(response);
        }

        const currentTrigger = Helper.enumsHelper().getClientStatusTriggerByStatus(this.individual.individualStatus);

        if (currentTrigger === trigger) {
            return Helper.createSuccessResponseObservable<boolean>(true);
        }

        const model = new IndividualStatusTriggerModel(this.individual.id, trigger);

        return this.individualsService.updateStatus(model)
            .pipe(
                map((statusResponse: ServerResponseData<null>) => {
                    if (statusResponse.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse<null>(
                            statusResponse,
                            IndividualRegistrationFormErrorStatesEnum.ChangeStatusFailed
                        );
                    }

                    this.individual.individualStatus = Helper.enumsHelper().getClientStatusByTrigger(trigger);

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    initForm(step: number): void {
        switch (step) {
            case PanelClientsAddIndividualStepsEnum.Individual:
                this.initIndividualStep();
                break;
            case PanelClientsAddIndividualStepsEnum.IdentityDetails:
                this.initIdentityDetailsStep();
                break;
            case PanelClientsAddIndividualStepsEnum.Addresses:
                this.initAddressesStep();
                break;
            case PanelClientsAddIndividualStepsEnum.UploadDocuments:
                this.initUploadDocumentsStep();
                break;

        }
    }

    validateForm(
        step: number,
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }
    ): boolean {
        switch (step) {
            case PanelClientsAddIndividualStepsEnum.Individual:
                const personalDetailsCheck = this.personalDetailsForm.validateForm(messageFunction);
                const nationalitiesCheck = this.nationalitiesForm.validateForm(messageFunction);

                return personalDetailsCheck && nationalitiesCheck;
            case PanelClientsAddIndividualStepsEnum.IdentityDetails:
                const identityDetailsCheck = this.identityDetailsForm.validateForm(messageFunction);
                const taxDetailsCheck = this.taxDetailsForm.validateForm(messageFunction);
                // AC-F1: the step cannot be completed until one of the two answers is given.
                const identificationNumberCheck = this.identificationNumberForm.validateForm(messageFunction);

                return identityDetailsCheck && taxDetailsCheck && identificationNumberCheck;
            case PanelClientsAddIndividualStepsEnum.Addresses:
                return this.addressesForm.validateForm(messageFunction);
            case PanelClientsAddIndividualStepsEnum.UploadDocuments:
                const proofOfIdentityCheck = this.proofOfIdentityForm.validateForm(messageFunction);
                const proofOfAddressCheck = this.proofOfAddressForm.validateForm(messageFunction);

                return proofOfIdentityCheck && proofOfAddressCheck;
        }

        return false;
    }

    createRequest(step: number): ServerResponse<boolean> {
        switch (step) {
            case PanelClientsAddIndividualStepsEnum.Individual:
                return this.createIndividualStepRequest();
            case PanelClientsAddIndividualStepsEnum.IdentityDetails:
                return this.createIdentityDetailsStepRequest();
            case PanelClientsAddIndividualStepsEnum.Addresses:
                return this.createAddressesStepRequest().pipe(
                    switchMap(this.changeIndividualStatus.bind(this, ClientStatusTriggersEnum.PendingDocuments))
                );
            case PanelClientsAddIndividualStepsEnum.UploadDocuments:
                return this.createUploadDocumentsStepRequest().pipe(
                    switchMap(this.changeIndividualStatus.bind(this, ClientStatusTriggersEnum.PendingAcceptance))
                );
        }

        return null;
    }
}
